import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckIcon, DownloadIcon, MailCheckIcon, ChevronRightIcon } from 'lucide-react';
import { Container, Kicker, Callout } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { Field, inputClass, ConsentCheckbox } from '../components/ui/Field';
import { getResource } from '../data/resources';
import { SECTORS } from '../data/services';
import { submitLead, requestDownloadUrl } from '../lib/leads';
import { track } from '../lib/analytics';
import { Seo } from '../components/Seo';
import { resourceSchema, breadcrumbSchema } from '../lib/seo';
import { NotFound } from './NotFound';
export function ResourcePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const resource = getResource(slug ?? '');
  const [email, setEmail] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [organisation, setOrganisation] = React.useState('');
  const [role, setRole] = React.useState('');
  const [sector, setSector] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [downloadUrl, setDownloadUrl] = React.useState<string | null>(null);
  if (!resource) return <NotFound data-id="element-1436" data-mp="3g4mdtxynuq4q" />;
  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await submitLead({
      email,
      firstName,
      organisation,
      role,
      sector,
      consent,
      source: `resource:${resource.slug}`
    });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'Something went wrong. Please try again.');
      return;
    }
    const download = await requestDownloadUrl(resource.fileName, {
      kind: 'resource',
      id: resource.slug
    });
    if (!download.url) {
      setError(download.error ?? 'We could not prepare your download. Please try again.');
      return;
    }
    setDownloadUrl(download.url);
    track('ebook_download', {
      resource: resource.slug,
      sector
    });
  };
  return <>
      <Seo title={`${resource.title}, free ${resource.format.toLowerCase()}`} description={resource.summary} path={`/resources/${resource.slug}`} image={resource.cover} jsonLd={[resourceSchema({
      title: resource.title,
      description: resource.summary,
      path: `/resources/${resource.slug}`,
      image: resource.cover
    }), breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Resources',
      path: '/resources'
    }, {
      name: resource.title,
      path: `/resources/${resource.slug}`
    }])]} data-id="element-1437" data-mp="2fwz5nmr8rr32" />

      <section className="surface-dark" data-id="element-1438" data-mp="1o4zye48nh1v4">
        <Container className="py-12 sm:py-16" data-id="element-1439" data-mp="1s7ejyw3pefq3">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-1440" data-mp="1s865fe3l6x2n">
            <ol className="flex flex-wrap items-center gap-2 text-small text-white/55" data-id="element-1441" data-mp="1knlrvjxq3oly">
              <li data-id="element-1442" data-mp="1fospamjz5i0k"><Link to="/" className="hover:text-white" data-id="element-1443" data-mp="1fosnqi4bt9yg">Home</Link></li>
              <li aria-hidden="true" data-id="element-1444" data-mp="1foizp02udcve"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1445" data-mp="1um1k5q8hl3x1" /></li>
              <li data-id="element-1446" data-mp="1uljgi8jhvnf3"><Link to="/resources" className="hover:text-white" data-id="element-1447" data-mp="1ulji2cz57vh7">Resources</Link></li>
              <li aria-hidden="true" data-id="element-1448" data-mp="1po9rgu00pdrs"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1449" data-mp="1pnqdfodhn9j1" /></li>
              <li aria-current="page" className="truncate text-white/80" data-id="element-1450" data-mp="11yfiinitk9ct">{resource.title}</li>
            </ol>
          </nav>
          <Kicker data-id="element-1451" data-mp="wzud89opmzhi">{resource.format}</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[32px] leading-tight text-white sm:text-[44px]" data-id="element-1452" data-mp="1bmxe8qc8rq9l">
            {resource.title}
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-1453" data-mp="16uw71itxkpqf">{resource.summary}</p>
        </Container>
      </section>

      <Container className="py-14 sm:py-20" data-id="element-1454" data-mp="j4s4leshaw10">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]" data-id="element-1455" data-mp="e8l5a4ul8is1">
          <div data-id="element-1456" data-mp="3dlsqasl7unfl">
            <img src={resource.cover} alt={resource.coverAlt} className="w-full max-w-[380px] rounded-md" data-id="element-1457" data-mp="3dmc8maes3du5" />
            <h2 className="mt-10 font-display text-[26px] text-ink" data-id="element-1458" data-mp="3hlbb7od1eyif">What is inside</h2>
            <ul className="mt-5 space-y-3" data-id="element-1459" data-mp="3ml5zrl9cbc0b">
              {resource.whatIsInside.map(item => <li key={item} className="flex gap-3 text-[16px] text-body" data-id="element-1460" data-mp="3reibiusx7obn">
                  <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-1461" data-mp="64gk3jn0aab" />
                  {item}
                </li>)}
            </ul>
            <h2 className="mt-10 font-display text-[26px] text-ink" data-id="element-1462" data-mp="972p8z50t5z8">Who it is for</h2>
            <p className="mt-4 max-w-prose text-[16px] text-body" data-id="element-1463" data-mp="e6f9rd8f60yn">{resource.forWho}</p>
            <p className="mt-4 text-small text-muted" data-id="element-1464" data-mp="e4b52t2p4xxb">
              {resource.pages} pages. Delivered as a PDF through a secure link that expires after fifteen minutes.
            </p>
          </div>

          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-1465" data-mp="28ej2606l3u1f">
            <div className="rounded-md border border-line bg-mist p-6 sm:p-8" data-id="element-1466" data-mp="28g3x3qwt9ezt">
              {downloadUrl ? <div data-id="element-1467" data-mp="23lpy7eixwpqe">
                  <div className="flex items-center gap-2 text-gold" data-id="element-1468" data-mp="1yoptb6xttt0v">
                    <MailCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-1469" data-mp="1yoziisb1rw5i" />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-1470" data-mp="1tqxzu8hj9yuk">Ready</span>
                  </div>
                  <h2 className="mt-4 font-display text-[24px] leading-tight text-ink" data-id="element-1471" data-mp="2rbaut080nli5">
                    Your download is ready
                  </h2>
                  <p className="mt-3 text-[15px] text-body" data-id="element-1472" data-mp="2hefwahbqtl3v">
                    We have also emailed the link to {email}, so you can come back to it. The link expires after
                    fifteen minutes and can be reissued from that email.
                  </p>
                  <a href={downloadUrl} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-gold px-5 py-3.5 text-[15px] font-medium text-charcoal transition-colors hover:bg-[#ff7440]" data-id="element-1473" data-mp="11ygurowmsncl">
                    <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1474" data-mp="18cmo223e2sxg" />
                    Download the PDF
                  </a>
                  <p className="mt-4 break-all text-small text-muted" data-id="element-1475" data-mp="yg9lsfts36r3">Secure link: {downloadUrl}</p>
                  <div className="mt-6 border-t border-line pt-6" data-id="element-1476" data-mp="kpeh7fqbbbtb">
                    <Button to="/contact" variant="secondary" className="w-full" data-id="element-1477" data-mp="kn1tsn1sc98v">
                      Book a discovery call
                    </Button>
                  </div>
                </div> : <form onSubmit={onSubmit} className="space-y-5" data-id="element-1478" data-mp="fq1o4d8ul8ia">
                  <div data-id="element-1479" data-mp="fqbc5vac15lc">
                    <h2 className="font-display text-[24px] leading-tight text-ink" data-id="element-1480" data-mp="1q8zcngvt2cy">Get your copy</h2>
                    <p className="mt-2 text-[15px] text-body" data-id="element-1481" data-mp="5ruiurnx8ew4">
                      Five fields. We use them to send the file and to tailor what we send you next.
                    </p>
                  </div>

                  <Field id="res-email" label="Work email" required error={error ?? undefined} data-id="element-1482" data-mp="3ea4ojyp7pl0f">
                    <input id="res-email" type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="you@organisation.org" className={inputClass} data-id="element-1483" data-mp="3s9e5j0l8sj5g" />
                  </Field>
                  <Field id="res-name" label="First name" required data-id="element-1484" data-mp="euq5fhl4df38">
                    <input id="res-name" type="text" required value={firstName} onChange={event => setFirstName(event.target.value)} className={inputClass} data-id="element-1485" data-mp="ety9g5nm1fcf" />
                  </Field>
                  <Field id="res-org" label="Organisation" required data-id="element-1486" data-mp="1r6jtgar501ne">
                    <input id="res-org" type="text" required value={organisation} onChange={event => setOrganisation(event.target.value)} className={inputClass} data-id="element-1487" data-mp="1r8cqqzfv19iq" />
                  </Field>
                  <Field id="res-role" label="Your role" required data-id="element-1488" data-mp="17isueogyltrl">
                    <input id="res-role" type="text" required value={role} onChange={event => setRole(event.target.value)} className={inputClass} data-id="element-1489" data-mp="17gg5frcsaj51" />
                  </Field>
                  <Field id="res-sector" label="Sector" required data-id="element-1490" data-mp="osb4ssrfbeag">
                    <select id="res-sector" required value={sector} onChange={event => setSector(event.target.value)} className={inputClass} data-id="element-1491" data-mp="opyqcpkvjm25">
                      <option value="" data-id="element-1492" data-mp="h8thr5ez0695">Please choose</option>
                      {SECTORS.map(item => <option key={item} value={item} data-id="element-1493" data-mp="c7cljn7fl7yw">
                          {item}
                        </option>)}
                    </select>
                  </Field>

                  <ConsentCheckbox id="res-consent" checked={consent} onChange={setConsent} data-id="element-1494" data-mp="3alq9d0pdx5d">
                    I am happy for Cavalla Solution to email me about this download and related insights. See our{' '}
                    <Link to="/legal/privacy" className="underline underline-offset-2" data-id="element-1495" data-mp="3plqz5slpwn06">
                      privacy notice
                    </Link>
                    .
                  </ConsentCheckbox>

                  <Button type="submit" size="lg" disabled={submitting} className="w-full" data-id="element-1496" data-mp="3boaryevlkp7d">
                    {submitting ? 'Preparing your file' : 'Send me the download'}
                  </Button>
                </form>}
            </div>

            <Callout className="mt-6" data-id="element-1497" data-mp="2tlpgx6jjuqhg">
              Files are held in private storage. Every download is issued through a signed link generated for that
              request, so the file path is never exposed.
            </Callout>
          </div>
        </div>
      </Container>
    </>;
}